import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL, ADMIN_API_BASE_URL, XMEDICAL_CONFIG } from '../../config';
import { LocalStorageService } from '../../storage';

/**
 * HTTP Interceptor that adds application identification headers to API requests.
 *
 * Headers added:
 * - `x-installation-id` - Unique identifier for this installation
 * - `x-application-version` - Current application version
 *
 * @example
 * { provide: HTTP_INTERCEPTORS, useClass: ApplicationInterceptor, multi: true }
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicationInterceptor implements HttpInterceptor {
  private baseUrl = inject(API_BASE_URL);
  private adminUrl = inject(ADMIN_API_BASE_URL);
  private config = inject(XMEDICAL_CONFIG);
  private localStorage = inject(LocalStorageService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.indexOf(this.adminUrl) > -1 || request.url.indexOf(this.baseUrl) > -1) {
      return this.addApplicationHeaders(request, next);
    }
    return next.handle(request);
  }

  private addApplicationHeaders(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.config.storageKeys) {
      throw new Error('XMedicalConfig.storageKeys is required but not provided');
    }

    const installKey = this.config.storageKeys.installationId;
    const installationId = this.localStorage.getItem(installKey);
    const appVersion = this.config.version;

    // Preserve existing headers
    const headerSettings: { [name: string]: string | string[]; } = {};
    for (const key of request.headers.keys()) {
      headerSettings[key] = request.headers.getAll(key);
    }

    if (installationId) { headerSettings['x-installation-id'] = installationId; }
    if (appVersion) { headerSettings['x-application-version'] = appVersion; }

    const newHeader = new HttpHeaders(headerSettings);
    const changedRequest = request.clone({ headers: newHeader });
    return next.handle(changedRequest);
  }
}
